import { useEffect, useState } from "react";

import { generatePath, Link, LinkProps, useParams } from "react-router-dom";

import { getInsights } from "@/api/endpoints/insights";
import { getAnchor } from "@/api/endpoints/mentions";
import { FILTER, INSIGHT_TYPE } from "@/constants";
import InsightBadge from "@/containers/InsightBadge";
import InsightText from "@/containers/InsightText";
import { ROUTES } from "@/routes_new";
import { Insight } from "@/types/Insight";
import { cx, pageFromFeedIndex, stringifyQuery } from "@/utils";
import Typography from "@components/Typography";
import { useQuery } from "@tanstack/react-query";

import Card from "../Card";

import topWidgetStyles from "../../TopWidgets.module.css";
import styles from "./InsightsSummary.module.css";

interface ParamTypes {
  orgUuid: string;
  uuid: string;
}

const InsightLink: React.FC<
  {
    insight: Insight;
    children: React.ReactNode;
  } & Omit<LinkProps, "to">
> = ({ insight, children, ...props }) => {
  const [to, setTo] = useState("");
  const { orgUuid, uuid } = useParams<ParamTypes>();

  useEffect(() => {
    const resolveLink = () => {
      const { link, type } = insight;

      if (type === INSIGHT_TYPE.HIGH_REACH_MENTION) {
        // Without orgUuid the app redirects to the user's default organization,
        // which is the wrong one for users belonging to several organizations.
        if (!orgUuid) return;

        const mentionUuid = insight.mention_ids[0];

        getAnchor(mentionUuid)
          .then((response) => {
            const { ok, data } = response;
            if (ok && data) {
              const url = `${generatePath(ROUTES.MENTION_DETAIL_ORGID, {
                orgUuid,
                uuid,
              })}?${stringifyQuery({
                [FILTER.PAGE]: pageFromFeedIndex(data.feed_index),
              })}#${mentionUuid}`;

              setTo(url);
            } else {
              throw new Error(response.statusText);
            }
          })
          .catch(() => {
            // Nothing returned. Don't link it.
          });
      } else {
        if (link) setTo(link);
      }
    };

    resolveLink();
  }, [insight, orgUuid, uuid]);

  if (!to) {
    return <>{children}</>;
  }

  return (
    <Link to={to} className={styles.link} {...props}>
      {children}
    </Link>
  );
};

const InsightSummary: React.FunctionComponent<{
  className?: string;
}> = ({ className }) => {
  const { uuid } = useParams<ParamTypes>();

  const { data, error } = useQuery({
    queryKey: [uuid],
    queryFn: () => getInsights(uuid, []),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  if (error || !data?.ok) return null;

  const insights = data.data;

  return insights.length > 0 ? (
    <Card className={className}>
      <Typography className={topWidgetStyles.title} margin="none">
        {L.SEARCH_TOP_WIDGET_INSIGHT_SUMMARY_TITLE}
      </Typography>

      <ul data-testid="insights-summary" className={cx(styles.list, className)}>
        {insights?.slice(0, 3).map((insight, index) => {
          return (
            <li
              key={index}
              data-testid={`insight-type-${insight.type}`}
              className={styles.li}
            >
              <InsightBadge type={insight.type} />
              <InsightLink
                insight={insight}
                data-testid={`insight-type-${insight.type}-link`}
              >
                <InsightText className={styles.insight} insight={insight} />
              </InsightLink>
            </li>
          );
        })}
      </ul>
    </Card>
  ) : null;
};

export default InsightSummary;
